// ─── useApplyImageDimensions ─────────────────────────────────────
// Reads `rsvp_page.image_dimensions` (a map of { url: { width, height } })
// and applies `width` + `height` HTML attributes to any matching `<img>`
// inside `.public-invite-page`. This lets the browser reserve the correct
// layout slot before each image loads, eliminating Cumulative Layout Shift.
//
// Strictly non-visual:
//   - We never change `style.width` / `style.height` (CSS still controls
//     the rendered size). The width/height ATTRIBUTES only seed the
//     intrinsic ratio — CSS rules win for actual painting.
//   - object-fit / object-position untouched.
//
// Walks at mount + on every URL change in the map. Uses a MutationObserver
// for late-rendering images (lazy galleries, sectioned canvases, etc.).

import { useEffect } from 'react';

export default function useApplyImageDimensions(imageDimensions) {
  useEffect(() => {
    if (!imageDimensions || typeof imageDimensions !== 'object') return undefined;
    const map = imageDimensions;
    if (Object.keys(map).length === 0) return undefined;

    const root = document.querySelector('.public-invite-page') || document.body;
    if (!root) return undefined;

    const applyTo = (img) => {
      try {
        // Skip canvas/collage images. These elements live inside an
        // absolute-positioned frame with explicit CSS width + aspectRatio.
        // Stamping intrinsic width/height HTML attributes here introduces
        // a competing intrinsic aspect ratio (= dims.width / dims.height),
        // which collides with the CSS `aspectRatio` and pushes the image
        // off its frame — observed as a vertical drift between editor
        // preview and the real phone (see RCA on section-v1 mismatch).
        // CLS prevention only matters for fluid in-flow `<img>` tags;
        // absolute canvas images already reserve their slot via the
        // frame's width/height/aspect-ratio style.
        if (img.closest && img.closest('[data-section-type="collage"]')) return;
        const src = img.currentSrc || img.src;
        if (!src) return;
        // Match by full URL or by ".../api/uploads/<filename>" suffix.
        let dims = map[src];
        if (!dims) {
          // Try matching by the /api/uploads/ tail in case the renderer
          // prefixed an absolute URL while the map stored the relative.
          for (const key of Object.keys(map)) {
            if (src.endsWith(key) || key.endsWith(src) || src.indexOf(key) >= 0) {
              dims = map[key];
              break;
            }
          }
        }
        if (!dims) return;
        if (!img.hasAttribute('width')) img.setAttribute('width', String(dims.width));
        if (!img.hasAttribute('height')) img.setAttribute('height', String(dims.height));
      } catch {
        /* ignore */
      }
    };

    const walk = () => {
      root.querySelectorAll('img').forEach(applyTo);
    };
    walk();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.tagName === 'IMG') applyTo(node);
          node.querySelectorAll?.('img').forEach(applyTo);
        });
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [imageDimensions]);
}
